import React, {useCallback, useEffect, useMemo, useState} from "react";
import ResponsiveContent from "../../../../layout/ResponsiveContent";
import {Avatar, Button, Pagination, Popover, Space, Table, Tag, Typography} from "antd";
import {AssetsTableRecord, BIODIVERSITY_WITHIN, ContactPeople} from "../../../../types/api/assets";
import {CustomLoading} from "../../../common/CustomLoading";
import {useTranslation} from "react-i18next";
import {useGetAssetsOptionsQuery, useLazyGetAssetsQuery} from "../../../../redux/api/assets";
import {useGetHazardTypeClassesQuery} from "../../../../redux/api/hazardTypes";
import {useGetAssetBiodiversityDataSourcesQuery} from "../../../../redux/api/asset";
import {
  fromFeToBeAssetsFilters,
  getFiltersOfTheAssetsTableStaticColumn,
  isAssetsTableStaticColumn
} from "../../../../types/internal/tables/assetsTable";
import {ColumnsType} from "antd/lib/table";
import ResetFiltersSortersCreationAreas from "./ResetFiltersSortersCreationAreas";
import ColumnsSelectionCreationAreas from "./ColumnsSelectionCreationAreas";
import DateFormat from "../../../../locales/DateFormat";
import _, {capitalize, debounce} from "lodash";
import {VALUE_NOT_PRESENT} from "../../../../utils/translation";
import CustomNumericFormat from "../../../common/customNumeric/CustomNumericFormat";
import {fromFeToBeSorter} from "../../../../utils/parser";
import useAssetsColumns from "../../../../customHooks/useAssetsColumns";
import useAssetsFilters from "../../../../customHooks/useAssetsFilters";
import useAssetsSorters from "../../../../customHooks/useAssetsSorters";
import AssetsTableColumnHeaderCreationAreas from "./AssetsTableColumnHeaderCreationAreas";
import AssignAssetsToClusterButton from "./AssignAssetsToClusterButton";
import {useGetAlertParametersTypeQuery} from "../../../../redux/api/alertParameters";
import {useParams} from "react-router-dom";
import {useEventContext} from "../../../../context/event/EventContext";
import {ReloadOutlined} from "@ant-design/icons";

export default function AssetsTableCreationAreas() {
  const {t} = useTranslation();
  const {uuid} = useParams()

  const {getClusterFromUuid} = useEventContext()

  const {
    columns,
    addColumns,
    removeColumns,
    resetColumns,
  } = useAssetsColumns();
  const {
    filters,
    addFilters,
    removeFilters,
    setOverallLandCoverageNullFilter,
    removeOverallLandCoverageFilter,
    setOverallLandCoverageMinFilter,
    removeOverallLandCoverageMinFilter,
    setOverallLandCoverageMaxFilter,
    removeOverallLandCoverageMaxFilter,
    resetFilters,
    filtersInExcludeMode,
    insertFilterInExcludeMode,
    removeFilterInExcludeMode,
  } = useAssetsFilters();
  const {
    sorters,
    addSorters,
    removeSorters,
    resetSorters,
  } = useAssetsSorters();
  const [pagination, setPagination] = useState({current: 1, pageSize: 10});

  const [
    getAssets,
    {
      data: assets,
      isFetching: isFetchingAssets,
      isUninitialized: isUninitializedAssets,
    }] = useLazyGetAssetsQuery();
  const {data: assetsOptions} = useGetAssetsOptionsQuery();

  const {
    data: hazardTypes,
    isFetching: isFetchingHazardTypes,
  } = useGetHazardTypeClassesQuery();
  const {data: alertParametersTypeSources} = useGetAlertParametersTypeQuery({paginate: false})
  const {
    data: biodiversitySources,
  } = useGetAssetBiodiversityDataSourcesQuery();

  const getColumnProps = useCallback((
    dataIndex: string | string[],
    key: string,
    render?: (value: any, record: any, index: number) => (React.ReactNode | any),
    width = 200,
    hidden = false
  ) => ({
    dataIndex: dataIndex,
    key: key,
    sorter: false,
    filter: false,
    title: <AssetsTableColumnHeaderCreationAreas
      columnKey={key}
      filters={filters}
      filtersInExcludeMode={filtersInExcludeMode}
      sorters={sorters}
      addFilters={addFilters}
      removeFilters={removeFilters}
      setOverallLandCoverageNullFilter={setOverallLandCoverageNullFilter}
      removeOverallLandCoverageFilter={removeOverallLandCoverageFilter}
      setOverallLandCoverageMinFilter={setOverallLandCoverageMinFilter}
      removeOverallLandCoverageMinFilter={removeOverallLandCoverageMinFilter}
      setOverallLandCoverageMaxFilter={setOverallLandCoverageMaxFilter}
      removeOverallLandCoverageMaxFilter={removeOverallLandCoverageMaxFilter}
      insertFilterInExcludeMode={insertFilterInExcludeMode}
      removeFilterInExcludeMode={removeFilterInExcludeMode}
      addSorters={addSorters}
      removeSorters={removeSorters}
    />,
    width,
    hidden,
    render
  }), [addFilters, addSorters, filters, filtersInExcludeMode, insertFilterInExcludeMode, removeFilterInExcludeMode, removeFilters, removeOverallLandCoverageFilter, removeOverallLandCoverageMaxFilter, removeOverallLandCoverageMinFilter, removeSorters, setOverallLandCoverageMaxFilter, setOverallLandCoverageMinFilter, setOverallLandCoverageNullFilter, sorters]);

  const hazardColumns: ColumnsType<AssetsTableRecord> = useMemo(() => hazardTypes ? hazardTypes.map(ht => getColumnProps(
    ['hazards', ht.code],
    ht.code,
    (v: number) => {
      switch (v) {
        case (-2):
          return VALUE_NOT_PRESENT;
        case (-1):
          return 'n.d.';
        default:
          return v;
      }
    },
    20
  )) : [], [hazardTypes, getColumnProps]);

  const alertParametersTypeColumns: ColumnsType<AssetsTableRecord> = useMemo(() => alertParametersTypeSources ? alertParametersTypeSources.map(param => getColumnProps(
    ['alert_parameters', param.code],
    param.code,
    (value: number | null) => {
      return typeof value === 'number' && <div>
        <CustomNumericFormat
          value={value}
          displayType={'text'}
          suffix={param?.um ? " " + param.um : ""}
          decimalScale={param?.decimals}
          fixedDecimalScale={true}
        />
      </div>
    },
    100
  )) : [], [alertParametersTypeSources, getColumnProps]);

  const coverageColumns: ColumnsType<AssetsTableRecord> = useMemo(() => (biodiversitySources ?? []).map(source => getColumnProps(
    ['overall_land_coverage'],
    source.value,
    (_, record) => {
      const coverage = record.overall_land_coverage[source.value]
      return typeof coverage === 'number' && <div
        style={{
          width: '100%',
          textAlign: 'right'
        }}
      >
        <CustomNumericFormat
          value={(coverage) * 100}
          displayType={'text'}
          suffix={" %"}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      </div>
    },
    40,
  )), [biodiversitySources, getColumnProps]);

  const allTheColumns: ColumnsType<AssetsTableRecord> = useMemo(() => [
    {
      ...getColumnProps('acoustic_zone', 'acoustic_zone'),
    },
    {
      ...getColumnProps(
        'address',
        'address',
        (value) => <div style={{textTransform: 'capitalize'}}>{value ? value.toLowerCase() : ''}</div>
      ),
    },
    {
      ...getColumnProps(
        'admin_level_1',
        'admin_level_1',
        (value) => <div style={{textTransform: 'capitalize'}}>{value ? value.toLowerCase() : ''}</div>
      )
    },
    {
      ...getColumnProps('admin_level_2', 'admin_level_2'),
    },
    {
      ...getColumnProps(
        'admin_level_3',
        'admin_level_3',
        (value) => <div style={{textTransform: 'capitalize'}}>{value ? value.toLowerCase() : ''}</div>
      )
    },
    {
      ...getColumnProps('building_type', 'building_type'),
    },
    {
      ...getColumnProps('building_type_detail', 'building_type_detail'),
    },
    {
      ...getColumnProps(
        'closing_date',
        'closing_date',
        (value) => <DateFormat>{value}</DateFormat>
      ),
    },
    {
      ...getColumnProps(
        'companies',
        'companies',
        (companies: string[] | null) => {
          if (companies) {
            return <Space size={10} wrap>
              {companies.map((c, idx) => (
                <Tag
                  style={{width: '12rem', textAlign: 'center'}}
                  key={idx}
                >
                  <Typography.Text
                    ellipsis={{tooltip: true}}
                  >
                    {c}
                  </Typography.Text>
                </Tag>),
              )}
            </Space>;
          }
        },
      ),
    },
    {
      ...getColumnProps(
        'contact_people',
        'contact_people',
        (cp: ContactPeople[] | null) => {
          if (cp) {
            if (cp.length === 1) {
              return <Popover
                content={
                  <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Typography.Text strong>{cp[0].full_name}</Typography.Text>
                    <Typography.Text>{cp[0].email}</Typography.Text>
                    <Typography.Text>{cp[0].phone_number}</Typography.Text>
                  </div>
                }
              >
                <span style={{textTransform: 'capitalize'}}>{cp[0].full_name.toLowerCase()}</span>
              </Popover>;
            } else {
              return <ul
                style={{padding: 0, listStyleType: 'none'}}
                onClick={e => e.stopPropagation()}
              >
                {cp.map((c, idx) => <Popover
                  key={idx}
                  content={
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                      <Typography.Text strong>{c.full_name}</Typography.Text>
                      <Typography.Text>{c.email}</Typography.Text>
                      <Typography.Text>{c.phone_number}</Typography.Text>
                    </div>
                  }
                >
                  <li
                    key={idx}
                    style={{textTransform: 'capitalize'}}
                  >
                    {c.full_name.toLowerCase()}
                  </li>
                </Popover>)}
              </ul>;
            }
          }
        },
      ),
    },
    {
      ...getColumnProps('country', 'country'),
    },
    {
      ...getColumnProps(
        'is_user_created',
        'is_user_created',
        (value: boolean | null) => {
          if (typeof value === 'boolean') {
            return capitalize(t(value ? 'assets.isUserCreated' : 'assets.isNotUserCreated'));
          }
        },
      ),
    },
    {
      ...getColumnProps(
        'ownership',
        'ownership',
        (v: string) => {
          return capitalize(assetsOptions?.ownership.find(option => option.value === v)?.label || '');
        },
      )
    },
    {
      ...getColumnProps('master_asset', 'master_asset'),
    },
    {
      ...getColumnProps(
        'opening_date',
        'opening_date',
        (value) => {
          return <DateFormat>{value}</DateFormat>;
        },
      ),
    },
    {
      ...getColumnProps(
        'ownership',
        'ownership',
        (value: string) => capitalize(assetsOptions?.ownership.find(el => el.value === value)?.label || '')
      ),
    },
    {
      ...getColumnProps(
        'postal_code',
        'postal_code'
      ),
    },
    {
      ...getColumnProps(
        'realestate_garrison',
        'realestate_garrison'
      ),
    },
    {
      ...getColumnProps(
        'realestate_hub',
        'realestate_hub'
      ),
    },
    {
      ...getColumnProps(
        'sigi_code',
        'sigi_code'
      ),
    },
    {
      ...getColumnProps(
        'biodiversity_within',
        'biodiversity_within',
        (_, record) => {
          let parsedValue = "";
          switch (record.biodiversity_within) {
            case BIODIVERSITY_WITHIN.IN:
              parsedValue = capitalize(t('assets.landCoverageIn'))
              break;
            case BIODIVERSITY_WITHIN.OUT:
              parsedValue = capitalize(t("assets.landCoverageOut"))
              break;
            case BIODIVERSITY_WITHIN.FULL:
              parsedValue = capitalize(t('assets.landCoverageFull'))
              break;
            case BIODIVERSITY_WITHIN.NA:
              parsedValue = VALUE_NOT_PRESENT
              break;
          }
          return parsedValue
        }
      ),
    },
    {
      ...getColumnProps(
        'surface_basement',
        'surface_basement',
      ),
    },
    {
      ...getColumnProps(
        'surface_total',
        'surface_total',
      ),
    },
    {
      ...getColumnProps(
        'total_resources',
        'total_resources',
      ),
    },
    {
      ...getColumnProps(
        'total_uog_contacts',
        'total_uog_contacts',
      ),
    },
    {
      ...getColumnProps(
        'event_cluster',
        'event_cluster',
        (value: string) => {
          const cluster = getClusterFromUuid(value)
          return cluster && <Space>
            <Avatar
              size={16}
              style={{backgroundColor: cluster.color_code}}
            />
            {cluster.name}
          </Space>
        }
      )
    },
    ...hazardColumns,
    ...alertParametersTypeColumns,
    ...coverageColumns,
  ], [getColumnProps, hazardColumns, alertParametersTypeColumns, coverageColumns, t, assetsOptions?.ownership, getClusterFromUuid]);

  const antdColumns = useMemo(() => {
    const antdColumns: ColumnsType<AssetsTableRecord> = [];

    columns.forEach(selectedColumn => {
      const foundColumn = allTheColumns.find(antdColumn => antdColumn.key === selectedColumn);

      if (foundColumn) {
        antdColumns.push(foundColumn);
      }
    });

    return antdColumns;
  }, [columns, allTheColumns]);

  const getAssetData = useCallback(() => {
    getAssets({
      page: pagination.current,
      page_size: pagination.pageSize,
      filters: {
        ...fromFeToBeAssetsFilters(filters, filtersInExcludeMode),
        event_uuid: uuid
      },
      sorters: fromFeToBeSorter(sorters),
    })
  }, [filters, filtersInExcludeMode, getAssets, pagination, sorters, uuid])

  useEffect(() => {
    const debouncedOnChange = debounce(() => getAssetData(), 700);

    debouncedOnChange()

    return () => {
      debouncedOnChange.cancel();
    }
  }, [getAssetData]);

  useEffect(() => {
    // remove filters if the column is not present
    let filtersToRemove: string[] = [];

    for (const [key, value] of Object.entries(filters)) {
      if (key === 'hazards') {
        if (filters.hazards) {
          const hazardsFilters = Object.keys(filters.hazards);
          filtersToRemove.push(...hazardsFilters);
        }
      } else if (key === "alert_parameters") {
        if (filters.alert_parameters) {
          const alertParametersFilters = Object.keys(filters.alert_parameters)
          filtersToRemove.push(...alertParametersFilters)
        }
      } else if (key === 'overall_land_coverage') {
        if (filters.overall_land_coverage) {
          const coverageFilters = Object.keys(filters.overall_land_coverage);
          filtersToRemove.push(...coverageFilters);
        }
      } else {
        filtersToRemove.push(key);
      }
    }

    columns.forEach((col) => {
      if (isAssetsTableStaticColumn(col)) {
        filtersToRemove = _.difference(filtersToRemove, getFiltersOfTheAssetsTableStaticColumn(col));
      } else {
        filtersToRemove = _.difference(filtersToRemove, [col]);
      }
    });

    if (filtersToRemove.length) {
      removeFilters(filtersToRemove);
    }
  }, [columns, filters, removeFilters]);

  return (
    <ResponsiveContent
      title={t('assets.assetsList')}
    >
      <div style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
        <Space wrap>
          <ResetFiltersSortersCreationAreas
            filters={filters}
            sorters={sorters}
            resetFilters={resetFilters}
            resetSorters={resetSorters}
            resetPagination={() => setPagination({current: 1, pageSize: 10})}
          />
          <ColumnsSelectionCreationAreas
            columns={columns}
            addColumns={addColumns}
            removeColumns={removeColumns}
            resetColumns={resetColumns}
          />
          {!!uuid && <AssignAssetsToClusterButton
            filters={filters}
            filtersInExcludeMode={filtersInExcludeMode}
            callbackUpdateTable={() => getAssetData()}
            eventUuid={uuid}
          />}
          <Button
            icon={<ReloadOutlined/>}
            onClick={() => getAssetData()}
            style={{marginLeft: 5}}
          >
            {capitalize(t('clusters.updateTable'))}
          </Button>
        </Space>
        <Table<AssetsTableRecord>
          columns={antdColumns}
          dataSource={assets?.results}
          rowKey={'uuid'}
          size={'middle'}
          scroll={{x: true}}
          pagination={false}
          loading={
            {
              spinning: isFetchingAssets || isUninitializedAssets || isFetchingHazardTypes,
              indicator: <CustomLoading/>,
            }
          }
          onRow={() => (
            {
              style: {
                cursor: 'pointer',
              },
            })
          }
        />
        <Pagination
          disabled={!assets?.count}
          showSizeChanger={true}
          total={assets?.count}
          showTotal={(total, range) => t('table.pageSizeOfTotal', {
            rangeStart: range[0],
            rangeEnd: range[1],
            total,
          })}
          onChange={(current, pageSize) => {
            setPagination({current, pageSize});
          }}
          {...pagination}
        />
      </div>
    </ResponsiveContent>
  );
}